import { useEffect, useMemo } from 'react';
import { isEmpty } from 'lodash';

import type { AgentTimelineEntry } from '~/components/AiChat/AgentTimeline/types';
import { useRealTimeToken } from '~/components/AiChat/hooks/useRealTimeToken';
import { useRealTimeUpdates } from '~/components/AiChat/hooks/useRealTimeUpdates';
import type { ProgressStatus } from '~/components/ProgressTimeline/types';
import { useDelayedRefresh } from '~/components/ProgressTimeline/useDelayedRefresh';
import { fnolTimelineFirestoreService } from '~/services/firestore/firestoreService';

import { toLatestStatus, toTimelineEntries } from './fnolProgressTimelineReducer';

// The terminal event can arrive before the CMS webhook has committed the item's new status, so one
// immediate refresh may still read `ai_processing`. Repeat a few times; the row leaving that status
// clears `isLive`, which cancels whatever is still scheduled.
const REFRESH_DELAYS_MS = [0, 3000, 6000];

export interface FnolProgressTimelineState {
  entries: AgentTimelineEntry[];
  /** The run's latest line — what it is doing now, or why it failed. */
  status?: ProgressStatus;
  /**
   * Whether the timeline is worth rendering at all. Empty rows alone can't decide it: they mean
   * "not yet" before the plan event and "never" for a finished run that emitted none.
   */
  shouldDisplay: boolean;
}

/**
 * Live FNOL processing timeline for a selected inbox item.
 *
 * 1. Fetches an inbox-item-scoped real-time token from the CMS backend
 *    (`GET /api/v1/fnol_inbox/inbox/{id}/fnol-timeline/token`), which is organization-scoped and
 *    issues a token for any item the user can see, so every selected item subscribes.
 * 2. Subscribes to Firestore through the shared {@link useRealTimeUpdates} hook, on a dedicated
 *    service instance so re-selecting a row cannot disconnect AI chat or STP notifications.
 * 3. Reduces the run's events to checklist rows — see {@link toTimelineEntries}.
 *
 * The events outlive the run: the token is keyed on the AI task, not the row's status, and the first
 * snapshot replays the whole history. So a reload, or a row whose run already ended, rebuilds the
 * same checklist instead of losing a failure's reason the moment the status leaves `ai_processing`.
 */
export const useFnolProgressTimeline = ({
  inboxItemId,
  isLive,
  onRunFinished,
}: {
  inboxItemId: number;
  isLive: boolean;
  onRunFinished?: () => void;
}): FnolProgressTimelineState => {
  // Organization-scoped, issued for any item the user can see; a row selected before its AI task id
  // commits gets null for a moment, which the shared hook retries.
  const issueTokenResponse = useRealTimeToken(`/api/v1/fnol_inbox/inbox/${inboxItemId}/fnol-timeline/token`);

  const { events, hasReceivedInitialSnapshot } = useRealTimeUpdates({
    issueTokenResponse,
    enabled: Boolean(issueTokenResponse),
    service: fnolTimelineFirestoreService,
  });

  const entries = useMemo(() => toTimelineEntries(events), [events]);
  const status = useMemo(() => toLatestStatus(events), [events]);

  // The reducer keeps exactly one row in progress until a terminal event lands, so a rendered
  // checklist with no in-progress row means the run finished — completed or failed.
  const isRunFinished = !isEmpty(entries) && entries.every((entry) => entry.status !== 'in_progress');

  // Read through a ref so an inline callback can't restart the refresh schedule on every render.
  const { schedule, cancel } = useDelayedRefresh(onRunFinished, REFRESH_DELAYS_MS);

  useEffect(() => {
    if (!isLive || !isRunFinished) return undefined;
    schedule();
    return cancel;
  }, [isLive, isRunFinished, schedule, cancel]);

  const shouldDisplay =
    Boolean(issueTokenResponse) && (!isEmpty(entries) || Boolean(status) || isLive || !hasReceivedInitialSnapshot);

  return { entries, status, shouldDisplay };
};

export default useFnolProgressTimeline;
